/**
 * Analytics module — PostHog integration.
 *
 * Provides thin wrappers around PostHog so every callsite stays clean and
 * the app works fine when PostHog isn't configured (dev, staging, tests).
 *
 * Setup: set VITE_POSTHOG_KEY in your .env.local to enable tracking.
 */

import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com';

let _initialised = false;

/**
 * Initialise PostHog. Safe to call multiple times — only runs once.
 * Call this in main.tsx or App.tsx before the first render.
 */
export function initAnalytics(): void {
  if (_initialised || !POSTHOG_KEY) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Automatically capture page views on route changes (SPA mode)
    capture_pageview: true,
    capture_pageleave: true,
    // Respect Do Not Track browser setting
    respect_dnt: true,
    // Persist across sessions
    persistence: 'localStorage+cookie',
    // Autocapture clicks/inputs — useful early on for exploration
    autocapture: true,
    // Don't load toolbar by default (opt-in via URL param)
    loaded: (ph) => {
      // In development, enable debug mode for console output
      if (import.meta.env.DEV) {
        ph.debug();
      }
    },
  });

  _initialised = true;
}

/** Whether PostHog is active (key present and initialised). */
function isActive(): boolean {
  return _initialised && !!POSTHOG_KEY;
}

// ── Public API ───────────────────────────────────────────────────────

/**
 * Track a named event with optional properties.
 * No-ops gracefully when PostHog isn't configured.
 */
export function trackEvent(
  name: string,
  properties?: Record<string, unknown>
): void {
  if (!isActive()) return;
  posthog.capture(name, properties);
}

/**
 * Identify the current user. Call after login / signup.
 * PostHog will merge anonymous events with the identified user.
 */
export function identifyUser(
  userId: string,
  traits?: Record<string, unknown>
): void {
  if (!isActive()) return;
  posthog.identify(userId, traits);
}

/**
 * Reset the current user identity. Call on logout.
 * Ensures the next visitor gets a fresh anonymous ID.
 */
export function resetUser(): void {
  if (!isActive()) return;
  posthog.reset();
}

// ── Typed Event Helpers (optional convenience layer) ────────────────

export const analytics = {
  signupStarted: (role: string) =>
    trackEvent('signup_started', { role }),

  signupCompleted: (role: string) =>
    trackEvent('signup_completed', { role }),

  login: () =>
    trackEvent('login'),

  maintenanceRequestCreated: (props?: Record<string, unknown>) =>
    trackEvent('maintenance_request_created', props),

  quoteSubmitted: (props?: Record<string, unknown>) =>
    trackEvent('quote_submitted', props),

  quoteApproved: (props?: Record<string, unknown>) =>
    trackEvent('quote_approved', props),

  jobCompleted: (props?: Record<string, unknown>) =>
    trackEvent('job_completed', props),

  checkoutStarted: (planId: string) =>
    trackEvent('checkout_started', { plan_id: planId }),

  subscriptionActivated: (planId?: string) =>
    trackEvent('subscription_activated', { plan_id: planId }),

  onboardingStarted: (role: string) =>
    trackEvent('onboarding_started', { role }),

  onboardingStepCompleted: (role: string, step: number, stepName: string) =>
    trackEvent('onboarding_step_completed', { role, step, step_name: stepName }),

  onboardingCompleted: (role: string, durationSeconds: number) =>
    trackEvent('onboarding_completed', { role, duration_seconds: durationSeconds }),

  onboardingSkipped: (role: string, step: number, stepName: string) =>
    trackEvent('onboarding_skipped', { role, step, step_name: stepName }),
} as const;
